import React, { useState, useRef } from 'react';
import { useAuth } from '../../../../auth/firebaseAuth';
import {
  isRenter,
  isLandlord,
  isAgent,
  isFsbo
} from '../../../../types/userTypes';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChatBubbleLeftRightIcon,
  InformationCircleIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  HomeIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

interface MessageInputProps {
  onSendMessage: (content: string) => Promise<void>;
  onSendAttachment: (file: File) => Promise<void>;
  disabled: boolean;
  attachmentsEnabled?: boolean;
}

interface QuickReplyTemplate {
  text: string;
  icon: React.ReactNode;
  category?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onSendAttachment,
  disabled,
  attachmentsEnabled = false
}) => {
  const [content, setContent] = useState('');
  const [isQuickRepliesOpen, setIsQuickRepliesOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userProfile } = useAuth();

  // Define role-based quick reply templates
  const getQuickReplies = (): QuickReplyTemplate[] => {
    if (isRenter(userProfile)) {
      return [
        {
          text: 'Hi! Is this property still available?',
          icon: <HomeIcon className="w-4 h-4" />,
          category: 'Availability'
        },
        {
          text: 'Could I schedule a viewing?',
          icon: <CalendarIcon className="w-4 h-4" />,
          category: 'Viewing'
        },
        {
          text: 'What is included in the rent?',
          icon: <CurrencyDollarIcon className="w-4 h-4" />,
          category: 'Details'
        },
        {
          text: 'Are pets allowed in this property?',
          icon: <InformationCircleIcon className="w-4 h-4" />,
          category: 'Details'
        },
        {
          text: 'When is the property available from?',
          icon: <CalendarIcon className="w-4 h-4" />,
          category: 'Availability'
        },
        {
          text: 'What are the lease terms?',
          icon: <DocumentTextIcon className="w-4 h-4" />,
          category: 'Details'
        }
      ];
    } else if (isLandlord(userProfile)) {
      return [
        {
          text: 'Yes, this property is still available!',
          icon: <CheckCircleIcon className="w-4 h-4" />,
          category: 'Availability'
        },
        {
          text: 'Viewings are available this weekend. What time suits you?',
          icon: <CalendarIcon className="w-4 h-4" />,
          category: 'Viewing'
        },
        {
          text: 'The rent includes utilities and internet access.',
          icon: <CurrencyDollarIcon className="w-4 h-4" />,
          category: 'Details'
        },
        {
          text: 'Unfortunately, pets are not permitted in this property.',
          icon: <InformationCircleIcon className="w-4 h-4" />,
          category: 'Policy'
        },
        {
          text: 'The property is available for immediate move-in.',
          icon: <HomeIcon className="w-4 h-4" />,
          category: 'Availability'
        },
        {
          text: 'The lease term is typically 12 months with a 1-month deposit.',
          icon: <DocumentTextIcon className="w-4 h-4" />,
          category: 'Details'
        }
      ];
    } else if (isAgent(userProfile)) {
      return [
        {
          text: 'Thank you for your interest! This property is still on the market.',
          icon: <CheckCircleIcon className="w-4 h-4" />,
          category: 'Availability'
        },
        {
          text: 'I can arrange a viewing at your convenience. When would suit you?',
          icon: <CalendarIcon className="w-4 h-4" />,
          category: 'Viewing'
        },
        {
          text: 'Let me provide you with the full property details and pricing.',
          icon: <InformationCircleIcon className="w-4 h-4" />,
          category: 'Details'
        },
        {
          text: 'I have similar properties that might interest you.',
          icon: <HomeIcon className="w-4 h-4" />,
          category: 'Options'
        },
        {
          text: 'Would you like to discuss financing options?',
          icon: <CurrencyDollarIcon className="w-4 h-4" />,
          category: 'Finance'
        },
        {
          text: 'I can send you the full legal documentation.',
          icon: <DocumentTextIcon className="w-4 h-4" />,
          category: 'Documentation'
        }
      ];
    } else if (isFsbo(userProfile)) {
      return [
        {
          text: 'Yes, I am selling this property privately.',
          icon: <CheckCircleIcon className="w-4 h-4" />,
          category: 'Status'
        },
        {
          text: 'Viewings available by appointment. When suits you?',
          icon: <CalendarIcon className="w-4 h-4" />,
          category: 'Viewing'
        },
        {
          text: 'All documentation is prepared and ready for review.',
          icon: <DocumentTextIcon className="w-4 h-4" />,
          category: 'Documentation'
        },
        {
          text: 'The asking price is negotiable for serious buyers.',
          icon: <CurrencyDollarIcon className="w-4 h-4" />,
          category: 'Pricing'
        },
        {
          text: 'I have a solicitor managing the sale process.',
          icon: <UserGroupIcon className="w-4 h-4" />,
          category: 'Legal'
        },
        {
          text: 'The property has a valid BER certificate.',
          icon: <InformationCircleIcon className="w-4 h-4" />,
          category: 'Compliance'
        }
      ];
    }

    // Default templates for any user
    return [
      {
        text: 'Hello! I am interested in this property.',
        icon: <ChatBubbleLeftRightIcon className="w-4 h-4" />,
        category: 'General'
      },
      {
        text: 'Could you provide more information?',
        icon: <InformationCircleIcon className="w-4 h-4" />,
        category: 'General'
      },
      {
        text: 'Thank you for your response!',
        icon: <CheckCircleIcon className="w-4 h-4" />,
        category: 'General'
      }
    ];
  };

  const quickReplies = getQuickReplies();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || disabled) return;

    // Clear immediately for optimistic UI feel
    const messageToSend = content;
    setContent('');
    await onSendMessage(messageToSend);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && !disabled) {
      await onSendAttachment(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleQuickReplySelect = (text: string) => {
    setContent(text);
    setIsQuickRepliesOpen(false);
  };

  return (
    <div className="bg-white border-t border-gray-200 safe-area-bottom">
      {/* Quick Replies Section */}
      <div className="px-3 md:px-4 pt-2 md:pt-3 pb-2 border-b border-gray-100">
        <button
          type="button"
          onClick={() => setIsQuickRepliesOpen(!isQuickRepliesOpen)}
          className="flex items-center gap-2 text-xs md:text-sm font-medium text-gray-700 hover:text-blue-600 active:text-blue-700 transition-colors touch-manipulation w-full"
        >
          <ChatBubbleLeftRightIcon className="w-4 h-4 flex-shrink-0" />
          <span>Quick Replies</span>
          {isQuickRepliesOpen ? (
            <ChevronUpIcon className="w-4 h-4 flex-shrink-0" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
            {quickReplies.length}
          </span>
        </button>

        {/* Quick Reply Templates */}
        {isQuickRepliesOpen && (
          <div className="mt-2 md:mt-3 space-y-2 max-h-40 md:max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-1 gap-2">
              {quickReplies.map((template, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleQuickReplySelect(template.text)}
                  disabled={disabled}
                  className="group flex items-start gap-2 p-2.5 text-left text-sm bg-gray-50 hover:bg-blue-50 active:bg-blue-100 border border-gray-200 hover:border-blue-300 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  <div className="flex-shrink-0 mt-0.5 text-gray-400 group-hover:text-blue-600 transition-colors">
                    {template.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-700 group-hover:text-blue-700 line-clamp-2">
                      {template.text}
                    </p>
                    {template.category && (
                      <span className="inline-block mt-1 text-[10px] text-gray-400 group-hover:text-blue-500 uppercase tracking-wide">
                        {template.category}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Message Input Form */}
      <form onSubmit={handleSubmit} className="p-3 md:p-4">
        <div className="flex items-end gap-2">
          {attachmentsEnabled && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="p-2 md:p-2 text-gray-400 hover:text-gray-600 active:text-gray-700 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation flex-shrink-0"
                aria-label="Attach file"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx"
              />
            </>
          )}

          <div className="flex-1 bg-gray-100 rounded-2xl px-3 md:px-4 py-2 md:py-2.5 focus-within:ring-2 focus-within:ring-blue-500 focus-within:bg-white transition-all min-w-0">
            <input
              type="text"
              className="w-full bg-transparent border-none focus:ring-0 text-sm md:text-base text-gray-800 placeholder-gray-500"
              placeholder="Type a message..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={disabled}
            />
          </div>

          <button
            type="submit"
            disabled={!content.trim() || disabled}
            className={`
              p-2.5 md:p-2 rounded-full transition-all shadow-sm touch-manipulation flex-shrink-0
              ${!content.trim() || disabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 hover:shadow-md active:scale-95'}
            `}
            aria-label="Send message"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 rotate-90"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>

        {/* Safety Notice */}
        <div className="mt-2 text-center">
          <p className="text-[10px] md:text-xs text-gray-400 leading-relaxed">
            Be cautious when communicating.{' '}
            <a
              href="https://rentsmart.ie/safety"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Safety guidelines
            </a>
          </p>
        </div>
      </form>
    </div>
  );
};
